import { v4 as uuidv4 } from 'uuid';

import { IKnmEvent248 } from 'APIPackage/KnmModule/interfaces/IKnmEvent248';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import {
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  DATE_FORMAT_ISO_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { getUavTypeSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IUavTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUavTypeSelectOption';

import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { getDictionarySelectOptionWithState } from '../../../CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { DictionaryUUIDsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { createKnmEventInspectorSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmEventInspectorSelectOption';
import { createInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/common/createInteractionCategorySelectOption';
import { IKnmErknmEventSubmittedDocumentsForm } from '../interfaces/IKnmErknmEventSubmittedDocumentsForm';
import { IKnmErknmFormEventInspector } from '../interfaces/IKnmErknmFormEventInspector';
import { convertInitialKnmDataEventsProtocolsToFormData } from './convertInitialKnmDataEventsProtocolsToFormData';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { IKnmErknmFormEventUav } from '../interfaces/IKnmErknmFormUav';
import { convertExpertKnmDataToFormValues } from './convertExpertsKnmDataToFormValues';
import { IKnmActExpertSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmActExpertSelectOption';

export const convertInitialKnmDataEventsToFormData = (
  events: IKnmEvent248[] | null | undefined,
  state: IRootState,
): IKnmErknmFormEvent[] | undefined => {
  return events?.map((item, index) => {
    const selectOption = item.event?.dictId
      ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
          state,
          item.event?.dictId,
          item.event?.dictVersionId,
        )
      : undefined;

    const uavTypeSelectOptions: IUavTypeSelectOption[] = getUavTypeSelectOptions(state);

    const formEvent: IKnmErknmFormEvent = {
      guid: item.guid,
      type: selectOption,
      ...(!selectOption && events[index]),
      startDate: convertInitialKnmDateToFormDate(item.startDate, DATE_FORMAT_LONG_FNS),
      stopDate: convertInitialKnmDateToFormDate(item.stopDate, DATE_FORMAT_LONG_FNS),
      dateCreate: item.dateCreate,
      interactionCategory: item?.interactionCategory
        ? createInteractionCategorySelectOption(item.interactionCategory)
        : undefined,
      inspectors: item.inspectors
        ? item.inspectors.map(x => {
            const eventInspectorForm: IKnmErknmFormEventInspector = {
              guid: x.guid,
              fullName: createKnmEventInspectorSelectOption(x.fullName, x.guid),
            };

            return eventInspectorForm;
          })
        : undefined,
      listId: item?.guid || uuidv4(),
      isNew: !!item.isNew,
      submittedDocuments: !item.submittedDocuments
        ? undefined
        : item.submittedDocuments.map(doc => {
            const document: IKnmErknmEventSubmittedDocumentsForm = {
              ...doc,
              isNew: false,
              requestDateTime: convertInitialKnmDateToFormDate(
                doc.requestDateTime,
                DATE_FORMAT_LONG_MINUTE_FNS,
                DATE_FORMAT_ISO_FNS,
              ),
              descriptionReactQuillEditor: handleReactQuillDeltaValue(
                doc.descriptionReactQuillEditor || doc.description || null,
              ),
              submissionDateTime: convertInitialKnmDateToFormDate(
                doc.submissionDateTime,
                DATE_FORMAT_LONG_MINUTE_FNS,
                DATE_FORMAT_ISO_FNS,
              ),
              submissionDeadline: convertInitialKnmDateToFormDate(doc.submissionDeadline, DATE_FORMAT_LONG_FNS),
              status: {
                code: doc.status?.code || '',
                id: doc.status?.id || 0,
                title: doc.status?.title || '',
                weight: doc.status?.weight || 0,
                label: doc.status?.title || '',
                value: doc.status?.code || '',
              },
            };

            return document;
          }),
      actionNotPerformed: item.actionNotPerformed,
      linkVideo: item.linkVideo,
      linkVks: item.linkVks,
      actionDescription: item.actionDescription,
      actionDescriptionReactQuillEditor: handleReactQuillDeltaValue(
        item.actionDescriptionReactQuillEditor || item.actionDescription,
      ),
      originalStopDate: item.originalStopDate,
      protocol: convertInitialKnmDataEventsProtocolsToFormData(item.protocol),
      isFilledFromLink: item.isFilledFromLink || false,
      isUavUsed: (item?.uavTechnologies && item.uavTechnologies?.length > 0) || false,
      uavTechnologies: item?.uavTechnologies?.map(x => {
        const eventUavForm: IKnmErknmFormEventUav = {
          guid: x.guid || uuidv4(),
          isNew: false,
          listId: uuidv4(),
          name: x.name || undefined,
          type: uavTypeSelectOptions.find(uavType => uavType.id === x.typeId) || undefined,
        };

        return eventUavForm;
      }),
      eventCopyNumber: item.eventCopyNumber || undefined,
      experts: item?.experts?.map(expert => {
        const eventExpert = convertExpertKnmDataToFormValues(expert);
        const result: IKnmActExpertSelectOption = {
          label: `${eventExpert.title} - ${eventExpert.type?.label}`,
          value: eventExpert,
          id: eventExpert.listId,
        };

        return result;
      }),
    };

    return formEvent;
  });
};
